import { Role } from '@auth/user';
import { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';

// Every leaf/section below shares the same shape, only id/title/icon/url/auth/children differ
const navItem = (
  id: string,
  title: string,
  icon: string,
  url?: string,
  auth?: Role[],
): FuseNavItemType => ({
  id,
  title,
  type: 'item',
  icon,
  ...(url !== undefined ? { url } : {}),
  ...(auth ? { auth } : {}),
});

const navCollapse = (
  id: string,
  title: string,
  icon: string,
  children: FuseNavItemType[],
  auth?: Role[],
): FuseNavItemType => ({
  id,
  title,
  type: 'collapse',
  icon,
  ...(auth ? { auth } : {}),
  children,
});

const misItem = (id: string, title: string, url: string): FuseNavItemType =>
  navItem(id, title, 'heroicons-outline:chart-pie', url);

const misSection = (
  id: string,
  title: string,
  children: FuseNavItemType[],
): FuseNavItemType =>
  navCollapse(id, title, 'heroicons-outline:chart-pie', children);

/**
 * Default navigation configuration
 * This is the base navigation structure that will be used when no config overrides exist
 */
export const defaultNavigationConfig: FuseNavItemType[] = [
	navItem('home', 'Home', 'heroicons-outline:home', '/home'),
	navItem('accreditation', 'Accreditation', 'heroicons-outline:check-badge', '/accreditation', [Role.Onboarding, Role.Admin, Role.Primary, Role.Technical_Support, Role.Domain_Manager]),
	navCollapse(
		'account',
		'Account',
		'heroicons-outline:briefcase',
		[
			navItem('details', 'General', 'heroicons-outline:identification', 'account/details'),
			navItem('address', 'Addresses', 'heroicons-outline:map-pin', 'account/addresses'),
			navItem('contacts', 'Contacts', 'heroicons-outline:user', 'account/contacts'),
			navItem('users', 'Users', 'heroicons-outline:user-group', 'account/users'),
			navItem('documents', 'Documents', 'heroicons-outline:document-text', 'account/documents'),
			navItem('epp', 'EPP', 'heroicons-outline:shield-check', 'account/epp'),
		],
		[Role.Admin, Role.Primary, Role.Billing, Role.Domain_Manager, Role.Technical_Support, Role.Onboarding]
	),
	navItem('onboarding', 'Onboarding', 'heroicons-outline:link', '/onboarding', [
		Role.Admin,
		Role.Primary,
		Role.Technical_Support
	]),
  navCollapse(
    'mis',
    'MIS',
    'heroicons-outline:document-chart-bar',
    [
      misSection('mis-age', 'DUMs', [
        misItem('mis-age-live', 'Active', 'reports/age/live'),
        misItem('mis-age-deleted', 'Deleted', 'reports/age/delete'),
      ]),
      misItem('mis-benchmark', 'Ry Benchmark', 'reports/benchmark'),
      misSection('mis-billing', 'Transactions', [
        misItem('mis-billing-base', 'Billing', 'reports/billing/base'),
        misItem(
          'mis-billing-per-year',
          'YoY',
          'reports/billing/year-on-year',
        ),
        misItem('mis-billing-per-zone', 'Zone', 'reports/billing/per-zone'),
      ]),
      misSection('mis-domain', 'Domain Stats', [
        misItem(
          'mis-domain-count',
          'Count',
          'reports/domain-count/count',
        ),
        misItem(
          'mis-domain-deletes',
          'Deletes',
          'reports/domain-count/deletes',
        ),
        misItem('mis-ranking', 'Ranking', 'reports/ranking'),
        misItem('mis-net', 'Net Movement', 'reports/net-movement'),
      ]),
      misSection('mis-movement', 'Movement', [
        misItem('mis-vertical', 'Vertical', 'reports/vertical-movement'),
        misItem(
          'mis-horizontal',
          'Horizontal',
          'reports/horizontal-movement',
        ),
        misItem('mis-renew', 'Renewal', 'reports/renew-movement'),
        misItem('mis-movement-yoy', 'YoY', 'reports/movement-yoy'),
      ]),
      misSection('mis-subword', 'Name Analysis', [
        misItem(
          'mis-subword-create',
          'Create',
          'reports/subword-frequency/create',
        ),
        misItem(
          'mis-subword-delete',
          'Delete',
          'reports/subword-frequency/delete',
        ),
        misItem(
          'mis-subword-renew',
          'Renew',
          'reports/subword-frequency/renew',
        ),
      ]),
      misSection('mis-renews', 'Renew Stats', [
        misItem(
          'mis-rolling-renew',
          'Rolling Renews',
          'reports/rolling-renew',
        ),
      ]),
    ],
    [Role.Admin, Role.Primary, Role.Billing, Role.Domain_Manager],
  ),
	navCollapse(
		'billing',
		'Billing',
		'heroicons-outline:banknotes',
		[
			navItem('overview', 'Overview', 'heroicons-outline:home', 'billing/overview'),
			navItem('successful-payments', 'Successful Payments', 'heroicons-outline:check-circle', 'billing/successful-payments'),
			navItem('failed-payments', 'Failed Payments', 'heroicons-outline:x-circle', 'billing/failed-payments'),
			navItem(
				'financial-documents',
				'Documents',
				'heroicons-outline:document-currency-dollar',
				'billing/financial-documents'
			),
			navItem('transactions', 'Transactions', 'heroicons-outline:credit-card', 'billing/transactions'),
			navItem('pricelist', 'Pricelist', 'heroicons-outline:queue-list', 'billing/pricelist')
		],
		[Role.Admin, Role.Billing, Role.Primary, Role.Domain_Manager]
	),
	navCollapse(
		'webrar',
		'WebRar',
		'heroicons-outline:adjustments-horizontal',
		[
			navItem('dashboard', 'Home', 'heroicons-outline:home', 'webrar/dashboard'),
      navItem('current-domains', 'Domains', 'heroicons-outline:queue-list', 'webrar/current-domains'),
			navItem('hosts', 'Hosts', 'heroicons-outline:server', 'webrar/hosts'),
			navItem('new-domain', 'Register a Domain', 'heroicons-outline:plus-circle', 'webrar/new-domain'),
			navCollapse('transfers', 'Transfers', 'heroicons-outline:arrows-right-left', [
				navItem('webrar-transfers-in', 'Incoming', 'heroicons-outline:arrow-down-tray', 'webrar/transfers/incoming'),
				navItem('webrar-transfers-out', 'Outgoing', 'heroicons-outline:arrow-up-tray', 'webrar/transfers/outgoing'),
				navItem('transfer-dashboard', 'Bulk', 'heroicons-outline:chart-pie', 'webrar/transfers/dashboard')
			]),
			navItem('webrar-contacts', 'Contacts', 'heroicons-outline:user-group', 'webrar/contacts'),
			navItem('messages', 'Messages', 'heroicons-outline:envelope-open', 'webrar/messages'),
			navCollapse(
				'zone-editor',
				'Zone Manager',
				'heroicons-outline:arrows-right-left',
				[
					navItem('templates', 'Templates', 'heroicons-outline:document-duplicate', 'webrar/zone-editor?type=templates'),
					navItem('webrar-zones', 'Zones', 'heroicons-outline:queue-list', 'webrar/zone-editor?type=zones')
				],
				[Role.Billing, Role.Primary, Role.Admin]
			),
			navItem('settings', 'Settings', 'heroicons-outline:cog-8-tooth', 'webrar/settings')
		],
		[Role.Domain_Manager, Role.Admin, Role.Technical_Support, Role.Primary]
	),
	navCollapse('support', 'Support', 'heroicons-outline:lifebuoy', [
		navItem('support-tickets', 'Raise a request', 'heroicons-outline:ticket')
	])
];

export default defaultNavigationConfig;
